"use client";

import { RiTicketLine } from "@remixicon/react";
import { isUpcoming } from "@workspace/shared-utils";
import { memo } from "react";
import { Spinner } from "@workspace/ui/components/spinner";
import { useInfiniteScroll } from "@/hooks/shared/use-infinite-scroll";
import type { ShowFacepileData } from "@/lib/data/shared/facepile-cached";
import { ArtistShowCard } from "../cards/artist-show-card";
import { ArtistEmptyState, ArtistSearchEmptyState } from "../states/";

// Show type with artist-specific data
interface ArtistShow {
	id: string;
	date: string;
	name: string;
	venue: {
		id: string;
		name: string;
		city?: string;
		state?: string;
		country?: string;
	};
	artists: Array<{
		id: string;
		name: string;
		slug: string;
		image_url?: string | null;
		role?: string; // headliner, opener, etc.
	}>;
}

interface ArtistShowsViewProps {
	shows: ArtistShow[];
	artistId: string;
	artistName: string;
	fetchNextPage: () => void;
	hasNextPage?: boolean;
	isFetchingNextPage: boolean;
	selectedYear: string;
	searchTerm?: string;
	sortBy?: string;
	onClearSearch?: () => void;
	onClearYear?: () => void;
	isActive?: boolean; // For infinite scroll activation
	facepileData?: ShowFacepileData;
}

export const ArtistShowsView = memo(function ArtistShowsView({
	shows,
	artistName,
	fetchNextPage,
	hasNextPage,
	isFetchingNextPage,
	selectedYear,
	searchTerm = "",
	sortBy = "",
	onClearSearch,
	onClearYear,
	isActive = true,
	facepileData = {},
}: ArtistShowsViewProps) {
	// Enable infinite scroll
	useInfiniteScroll({
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isActive,
	});

	// Note: Filtering is already done server-side via tRPC query
	// No need to filter client-side

	if (shows.length === 0) {
		const hasFilters = searchTerm.trim() !== "" || selectedYear !== "all";

		if (hasFilters && onClearSearch && onClearYear) {
			return (
				<ArtistSearchEmptyState
					searchTerm={searchTerm}
					selectedYear={selectedYear}
					contentType="shows"
					artistName={artistName}
					onClearSearch={onClearSearch}
					onClearYear={onClearYear}
				/>
			);
		}

		return (
			<ArtistEmptyState
				icon={RiTicketLine}
				title={
					selectedYear === "all"
						? `${artistName} hasn't performed any shows yet`
						: `${artistName} didn't perform any shows in ${selectedYear}`
				}
				description="When they perform at shows and concerts, they'll show up here"
			/>
		);
	}

	// Separate upcoming and past shows using shared utility with grace period
	const upcomingShows = shows.filter((show) => {
		return isUpcoming(show.date);
	});
	const pastShows = shows.filter((show) => {
		return !isUpcoming(show.date);
	});

	// Deduplicate shows to prevent duplicate keys across sections
	const allShowIds = new Set();
	const deduplicatedUpcoming = upcomingShows.filter((show) => {
		if (allShowIds.has(show.id)) {
			return false;
		}
		allShowIds.add(show.id);
		return true;
	});
	const deduplicatedPast = pastShows.filter((show) => {
		if (allShowIds.has(show.id)) {
			return false;
		}
		allShowIds.add(show.id);
		return true;
	});

	// Determine section order based on sort preference
	const isOldestFirst = sortBy === "date-asc";
	const sections = isOldestFirst
		? [
				{ data: deduplicatedPast, title: "Past", keyPrefix: "past" },
				{
					data: deduplicatedUpcoming,
					title: "Upcoming",
					keyPrefix: "upcoming",
				},
			]
		: [
				{
					data: deduplicatedUpcoming,
					title: "Upcoming",
					keyPrefix: "upcoming",
				},
				{ data: deduplicatedPast, title: "Past", keyPrefix: "past" },
			];

	return (
		<div className="space-y-6">
			{sections.map(
				({ data, title, keyPrefix }) =>
					data.length > 0 && (
						<div key={keyPrefix}>
							<h3 className="mb-4 font-semibold text-foreground text-lg">
								{title}
							</h3>
							<div className="space-y-3">
								{data.map((show) => (
									<ArtistShowCard
										key={`${keyPrefix}-${show.id}`}
										show={show}
										attendees={facepileData[show.id] || []}
										isFacepileLoading={false}
									/>
								))}
							</div>
						</div>
					),
			)}

			{/* Loading indicator for infinite scroll */}
			{isFetchingNextPage && (
				<div className="flex justify-center py-4">
					<Spinner variant="muted" />
				</div>
			)}
		</div>
	);
});

ArtistShowsView.displayName = "ArtistShowsView";
